import React, { useState, useEffect } from 'react';
import { useParams, Link } from "react-router-dom";
import SEO from '../components/SEO';
import Header from "../partials/header/Header";
import Footer from '../container/Footer/Footer';
import ScrollToTop from '../components/ScrollToTop.jsx';

const Blog = () => {
    let { id } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchBlog();
    }, [id]);

    const fetchBlog = async () => {
        try {
            setLoading(true);
            const response = await fetch(`https://admin.lumademand.com/public/api/blogs/${id}`);
            const result = await response.json();
            
            if (result.success) {
                setBlog(result.data);
                setError(null);
            } else {
                setError(result.message || 'Blog post not found');
            }
        } catch (err) {
            setError('Error fetching blog post: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    if (loading) {
        return (
            <React.Fragment>
                <SEO title="Loading Blog..." />
                <Header />
                <div className="blog-details-container">
                    <div className="container">
                        <div className="blog-details__loading">
                            <div className="spinner"></div>
                            <p>Loading blog post...</p>
                        </div>
                    </div>
                </div>
                <Footer />
                <ScrollToTop />
                <style >{`
                    .blog-details-container {
                        padding: 4rem 0;
                        min-height: 60vh;
                        background: #f8f9fa;
                    }
                    .blog-details__loading {
                        text-align: center;
                        padding: 4rem 2rem;
                    }
                    .spinner {
                        width: 50px;
                        height: 50px;
                        border: 3px solid #f3f3f3;
                        border-top: 3px solid #ff4444;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin: 0 auto 1rem;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </React.Fragment>
        );
    }

    if (error || !blog) {
        return (
            <React.Fragment>
                <SEO title="Blog Not Found" />
                <Header />
                <div className="blog-details-container">
                    <div className="container">
                        <div className="blog-details__error">
                            <i className="fas fa-exclamation-circle"></i>
                            <h2>Oops! Blog Post Not Found</h2>
                            <p>{error || 'The blog post you are looking for does not exist.'}</p>
                            <Link to="/blog" className="blog-details__back-btn">
                                ← Back to Blog List
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
                <ScrollToTop />
                <style >{`
                    .blog-details-container {
                        padding: 4rem 0;
                        min-height: 60vh;
                        background: #f8f9fa;
                    }
                    .blog-details__error {
                        text-align: center;
                        padding: 4rem 2rem;
                        background: #fff;
                        border-radius: 12px;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        max-width: 600px;
                        margin: 0 auto;
                    }
                    .blog-details__error i {
                        font-size: 4rem;
                        color: #ff4444;
                        margin-bottom: 1rem;
                    }
                    .blog-details__error h2 {
                        color: #1a1a1a;
                        margin-bottom: 1rem;
                        font-size: 1.5rem;
                    }
                    .blog-details__error p {
                        color: #666;
                        margin-bottom: 1.5rem;
                    }
                    .blog-details__back-btn {
                        display: inline-block;
                        background: #ff4444;
                        color: white;
                        text-decoration: none;
                        padding: 0.75rem 1.5rem;
                        border-radius: 6px;
                        font-weight: 600;
                        transition: background 0.3s ease;
                    }
                    .blog-details__back-btn:hover {
                        background: #cc0000;
                    }
                `}</style>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <SEO title={blog.title} />
            <Header />
            
            <div className="blog-details-container bg-light">
                <div className="container">
                    {/* Breadcrumb */}
                    <div className="blog-details__breadcrumb">
                        <Link to="/">Home</Link>
                        <span className="separator">/</span>
                        <Link to="/blog">Blog</Link>
                        <span className="separator">/</span>
                        <span className="current">{blog.title}</span>
                    </div>

                    {/* Blog Header */}
                    <div className="blog-details__header">
                        <h1 className="blog-details__title">{blog.title}</h1>
                        <div className="blog-details__meta">
                            <div className="blog-details__author">
                                <i className="fas fa-user"></i>
                                <span>By {blog.author}</span>
                            </div>
                            <div className="blog-details__date">
                                <i className="fas fa-calendar"></i>
                                <span>{formatDate(blog.post_date)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Featured Image */}
                    {blog.image_url && (
                        <div className="blog-details__featured-image w-50 mx-auto">
                            <img src={blog.image_url} alt={blog.title} />
                        </div>
                    )}

                    {/* Blog Content */}
                    <div className="blog-details__content">
                        <div dangerouslySetInnerHTML={{ __html: blog.content }} />
                    </div>

                    {/* Back to Blog Button */}
                    <div className="blog-details__footer">
                        <Link to="/work" className="blog-details__back-button">
                            <i className="fas fa-arrow-left"></i>
                            Back to All Posts
                        </Link>
                    </div>
                </div>
            </div>

            <Footer />
            <ScrollToTop />

            {/* All CSS Styles */}
            <style >{`
                .blog-details-container {
                    padding: 3rem 0 5rem;
                   
                    min-height: 70vh;
                }

                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 1rem;
                }

                /* Breadcrumb Styles */
                .blog-details__breadcrumb {
                    margin-bottom: 2rem;
                    font-size: 0.875rem;
                    color: #666;
                }

                .blog-details__breadcrumb a {
                    color: #ff4444;
                    text-decoration: none;
                    transition: color 0.3s ease;
                }

                .blog-details__breadcrumb a:hover {
                    color: #cc0000;
                }

                .blog-details__breadcrumb .separator {
                    margin: 0 0.5rem;
                    color: #999;
                }

                .blog-details__breadcrumb .current {
                    color: #333;
                }

                /* Blog Header */
                .blog-details__header {
                    text-align: center;
                    margin-bottom: 2rem;
                }

                .blog-details__title {
                    font-size: 2.5rem;
                    font-weight: 800;
                    color: #1a1a1a;
                    margin-bottom: 1rem;
                    line-height: 1.3;
                }

                .blog-details__meta {
                    display: flex;
                    justify-content: center;
                    gap: 2rem;
                    flex-wrap: wrap;
                }

                .blog-details__author,
                .blog-details__date {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #666;
                    font-size: 0.875rem;
                }

                .blog-details__author i,
                .blog-details__date i {
                    color: #ff4444;
                }

                /* Featured Image */
                .blog-details__featured-image {
                    margin-bottom: 2rem;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }

                .blog-details__featured-image img {
                    width: 100%;
                    height: auto;
                    display: block;
                }

                /* Blog Content */
                .blog-details__content {
                    background: #fff;
                    padding: 3rem;
                    border-radius: 12px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                    margin-bottom: 2rem;
                }

                .blog-details__content :global(p) {
                    margin-bottom: 1.5rem;
                    line-height: 1.8;
                    color: #333;
                    font-size: 1rem;
                }

                .blog-details__content :global(h2) {
                    font-size: 1.75rem;
                    margin: 1.5rem 0 1rem;
                    color: #1a1a1a;
                }

                .blog-details__content :global(h3) {
                    font-size: 1.5rem;
                    margin: 1.5rem 0 1rem;
                    color: #1a1a1a;
                }

                .blog-details__content :global(h4) {
                    font-size: 1.25rem;
                    margin: 1.25rem 0 0.75rem;
                    color: #1a1a1a;
                }

                .blog-details__content :global(ul),
                .blog-details__content :global(ol) {
                    margin: 1rem 0 1.5rem;
                    padding-left: 2rem;
                }

                .blog-details__content :global(li) {
                    margin-bottom: 0.5rem;
                    line-height: 1.6;
                    color: #333;
                }

                .blog-details__content :global(blockquote) {
                    border-left: 4px solid #ff4444;
                    padding: 1rem 2rem;
                    margin: 1.5rem 0;
                    background: #f9f9f9;
                    font-style: italic;
                    color: #666;
                }

                .blog-details__content :global(img) {
                    max-width: 100%;
                    height: auto;
                    margin: 1.5rem 0;
                    border-radius: 8px;
                }

                .blog-details__content :global(a) {
                    color: #ff4444;
                    text-decoration: none;
                }

                .blog-details__content :global(a:hover) {
                    color: #cc0000;
                    text-decoration: underline;
                }

                /* Footer */
                .blog-details__footer {
                    text-align: center;
                }

                .blog-details__back-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: #ff4444;
                    color: white;
                    text-decoration: none;
                    padding: 0.75rem 1.5rem;
                    border-radius: 6px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                }

                .blog-details__back-button:hover {
                    background: #cc0000;
                    transform: translateX(-5px);
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .blog-details-container {
                        padding: 2rem 0 3rem;
                    }

                    .blog-details__title {
                        font-size: 1.75rem;
                    }

                    .blog-details__meta {
                        gap: 1rem;
                    }

                    .blog-details__content {
                        padding: 1.5rem;
                    }

                    .blog-details__content :global(p) {
                        font-size: 0.95rem;
                        line-height: 1.7;
                    }

                    .blog-details__content :global(h2) {
                        font-size: 1.5rem;
                    }

                    .blog-details__content :global(h3) {
                        font-size: 1.25rem;
                    }

                    .blog-details__breadcrumb {
                        font-size: 0.75rem;
                    }
                }

                @media (max-width: 480px) {
                    .blog-details__title {
                        font-size: 1.5rem;
                    }

                    .blog-details__content {
                        padding: 1rem;
                    }
                }
            `}</style>
        </React.Fragment>
    );
};

export default Blog;